/**
 * Contact Sync Migration Runner cards — iter 291.
 *
 * Three cards, one per migration, all sharing the same structure the
 * ContactGroupingRunnerCard already established:
 *   Phase 1 · Dry-Run  (GET-like, no writes) → renders report inline.
 *   Phase 2 · Apply    (POST) → gated by (a) completed dry-run + (b) typed "APPLY".
 *   Phase 3 · Rollback (POST) → paste the batch_id returned by Apply.
 *
 * Super-admin only. No curl, no console. All three read from
 *   /api/admin/contact-sync-migrations/{migration}/{dry-run|apply|rollback}
 *
 * data-testids:
 *   contact-sync-migration-{0042|0043|0044}-card
 *   contact-sync-migration-{key}-dryrun-btn
 *   contact-sync-migration-{key}-apply-confirm-input
 *   contact-sync-migration-{key}-apply-btn
 *   contact-sync-migration-{key}-batch-id
 *   contact-sync-migration-{key}-rollback-batch-input
 *   contact-sync-migration-{key}-rollback-btn
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Play, ChevronDown, ChevronRight, Loader2, CheckCircle2, AlertTriangle,
  Copy, Undo2, Database,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const MIGRATIONS = [
  {
    key: '0042_contact_sync_targets',
    label: '0042 · Contact Sync Targets seed',
    summary: 'Seeds db.contact_sync_targets from existing contacts with external_ref, then strips external_ref + google_sync from db.contacts (30-day backup).',
    reportKeys: [
      ['would_seed_targets', 'Targets to seed', true],
      ['skipped_existing', 'Skipped (already seeded)'],
      ['skipped_no_owner', 'Skipped (no owner inferable)', false, true],
      ['would_strip_from_contacts', 'Contacts to strip', true],
    ],
    applyResultKeys: [
      ['inserted_targets', 'Target rows inserted'],
      ['backed_up', 'Backup snapshots written'],
    ],
  },
  {
    key: '0043_user_contact_sync_prefs',
    label: '0043 · User Contact Sync Prefs fold-in',
    summary: 'Folds legacy google_tokens + google_sync_prefs into contact_sync_prefs.providers.google. Preserves values.',
    reportKeys: [
      ['users_affected', 'Users to migrate', true],
    ],
    applyResultKeys: [
      ['users_updated', 'Users updated'],
    ],
  },
  {
    key: '0044_contact_categories_collapse',
    label: '0044 · Contact Categories collapse (Subcontractor)',
    summary: 'Collapses 8 legacy "Subcontractor - X" categories to a single "Subcontractor", with trade code appended to spec_categories (90-day backup).',
    reportKeys: [
      ['would_update', 'Contacts to collapse', true],
    ],
    applyResultKeys: [
      ['updated', 'Contacts updated'],
    ],
  },
];


export default function ContactSyncMigrationsRunnerCard() {
  const [hidden, setHidden] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/me`, { headers: authHeaders() })
      .then((res) => {
        const user = res.data?.user || res.data || {};
        if (user.is_super_admin) {
          setHidden(false);
          setAdminEmail(user.email || '');
        }
      })
      .catch(() => {});
  }, []);

  if (hidden) return null;

  return (
    <Card className="border-stone-300" data-testid="contact-sync-migrations-runner-card">
      <CardHeader
        className="cursor-pointer flex flex-row items-start justify-between gap-4"
        onClick={() => setExpanded(!expanded)}
        data-testid="contact-sync-migrations-runner-toggle"
      >
        <div className="flex items-start gap-3">
          {expanded ? <ChevronDown className="h-5 w-5 mt-0.5" /> : <ChevronRight className="h-5 w-5 mt-0.5" />}
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Database className="h-4 w-4" />
              Contact Sync Architecture Migrations (iter 291)
              <Badge className="bg-stone-100 text-stone-700 border-stone-300 border ml-1">
                super-admin · tenant-scoped
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              Three migrations for the provider-agnostic contact-sync architecture.
              Each has its own Dry-Run / Apply / Rollback block. Apply is
              typed-confirmation gated. Apply returns a
              <code className="px-1 mx-0.5 text-[10px] bg-stone-100 rounded">batch_id</code>
              that pastes straight into the Rollback field.
              {adminEmail && (
                <span className="block mt-1 text-[11px] text-stone-500">
                  Signed in as <strong>{adminEmail}</strong>
                </span>
              )}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-6">
          {MIGRATIONS.map(mig => (
            <MigrationBlock key={mig.key} migration={mig} />
          ))}
        </CardContent>
      )}
    </Card>
  );
}


function MigrationBlock({ migration }) {
  const { key, label, summary, reportKeys, applyResultKeys } = migration;
  const [dryRunning, setDryRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunAt, setDryRunAt] = useState(null);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [confirmInput, setConfirmInput] = useState('');

  const [rollingBack, setRollingBack] = useState(false);
  const [rollbackBatchId, setRollbackBatchId] = useState('');
  const [rollbackResult, setRollbackResult] = useState(null);

  const runDryRun = useCallback(async () => {
    setDryRunning(true);
    setDryRunResult(null);
    setApplyResult(null);
    setConfirmInput('');
    try {
      const res = await axios.post(
        `${API}/api/admin/contact-sync-migrations/${key}/dry-run`, {},
        { headers: authHeaders() },
      );
      setDryRunResult(res.data);
      setDryRunAt(new Date());
      toast.success(`Dry-run complete — ${label}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dry-run failed');
    } finally {
      setDryRunning(false);
    }
  }, [key, label]);

  const runApply = useCallback(async () => {
    if (confirmInput.trim() !== 'APPLY') {
      toast.error('Type APPLY exactly to confirm');
      return;
    }
    if (!dryRunResult) {
      toast.error('Run a dry-run first');
      return;
    }
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/api/admin/contact-sync-migrations/${key}/apply`, {},
        { headers: authHeaders() },
      );
      setApplyResult(res.data);
      setConfirmInput('');
      setRollbackBatchId(res.data.batch_id || '');
      toast.success(`Applied — batch ${(res.data.batch_id || '').slice(0, 16)}…`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  }, [confirmInput, dryRunResult, key]);

  const runRollback = useCallback(async () => {
    if (!rollbackBatchId.trim()) {
      toast.error('Paste a batch_id first');
      return;
    }
    setRollingBack(true);
    setRollbackResult(null);
    try {
      const res = await axios.post(
        `${API}/api/admin/contact-sync-migrations/${key}/rollback`,
        { batch_id: rollbackBatchId.trim() },
        { headers: authHeaders() },
      );
      setRollbackResult(res.data);
      toast.success('Rollback complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rollback failed');
    } finally {
      setRollingBack(false);
    }
  }, [rollbackBatchId, key]);

  const copyBatchId = useCallback(async () => {
    if (!applyResult?.batch_id) return;
    try {
      await navigator.clipboard.writeText(applyResult.batch_id);
      toast.success('batch_id copied');
    } catch {
      toast.error('Copy failed');
    }
  }, [applyResult]);

  return (
    <div
      className="rounded-lg border border-stone-300 bg-white p-4 space-y-4"
      data-testid={`contact-sync-migration-${key}-card`}
    >
      <div>
        <h3 className="text-sm font-semibold">{label}</h3>
        <p className="text-xs text-stone-600 mt-1">{summary}</p>
      </div>

      {/* Phase 1: Dry-Run */}
      <div className="rounded-md border border-stone-200 bg-stone-50 p-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold">Step 1 · Dry-Run</p>
            <p className="text-[11px] text-stone-600">
              Read-only. Reports counts and samples of what would change.
            </p>
          </div>
          <Button
            size="sm"
            onClick={runDryRun}
            disabled={dryRunning}
            data-testid={`contact-sync-migration-${key}-dryrun-btn`}
          >
            {dryRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
            Run Dry-Run
          </Button>
        </div>

        {dryRunResult && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-2 border-t border-stone-200"
               data-testid={`contact-sync-migration-${key}-dryrun-report`}>
            {reportKeys.map(([field, label, highlight, warn]) => (
              <Stat key={field} label={label} value={dryRunResult[field]} highlight={highlight} warn={warn} />
            ))}
            {dryRunAt && (
              <Stat label="Dry-run captured" value={dryRunAt.toLocaleTimeString()} />
            )}
          </div>
        )}

        {/* 0044 special: show category breakdown from the dry-run */}
        {key === '0044_contact_categories_collapse' && dryRunResult?.by_category && (
          <div className="pt-2 border-t border-stone-200">
            <p className="text-[11px] font-semibold mb-1">By legacy category:</p>
            <ul className="text-[11px] font-mono space-y-0.5">
              {Object.entries(dryRunResult.by_category).map(([cat, count]) => (
                <li key={cat}>{cat} → {count}</li>
              ))}
            </ul>
          </div>
        )}

        {/* 0042 special: show sample of target rows about to be seeded */}
        {key === '0042_contact_sync_targets' && dryRunResult?.sample?.length > 0 && (
          <div className="pt-2 border-t border-stone-200">
            <p className="text-[11px] font-semibold mb-1">First 5 targets:</p>
            <ul className="text-[10px] font-mono space-y-0.5 max-h-32 overflow-y-auto">
              {dryRunResult.sample.map((s, i) => (
                <li key={i} className="text-stone-700 truncate">
                  {(s.contact_id || '').slice(0, 8)}… → user {(s.target_user_id || '').slice(0, 8)}… · {s.external_ref}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 0043 special: users about to be folded */}
        {key === '0043_user_contact_sync_prefs' && dryRunResult?.sample?.length > 0 && (
          <div className="pt-2 border-t border-stone-200">
            <p className="text-[11px] font-semibold mb-1">Sample users:</p>
            <ul className="text-[10px] font-mono space-y-0.5">
              {dryRunResult.sample.map((s, i) => (
                <li key={i} className="text-stone-700">
                  {(s.user_id || '').slice(0, 8)}… · has_tokens={String(s.has_google_tokens)} · has_prefs={String(s.has_google_sync_prefs)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Phase 2: Apply */}
      <div className={`rounded-md border p-3 space-y-3 ${
        dryRunResult ? 'border-rose-200 bg-rose-50/40' : 'border-stone-200 bg-stone-50/60 opacity-60'
      }`}>
        <div>
          <p className="text-xs font-semibold flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
            Step 2 · Apply
          </p>
          <p className="text-[11px] text-stone-600">
            Writes under a fresh <code className="text-[10px] bg-stone-100 px-1 rounded">batch_id</code>.
            Reversible via Step 3.
          </p>
        </div>
        {!dryRunResult && <p className="text-[11px] text-stone-500 italic">Run a dry-run first.</p>}
        {dryRunResult && !applyResult && (
          <div className="space-y-2">
            <p className="text-[11px] text-rose-900">
              Type <strong>APPLY</strong> to confirm.
            </p>
            <Input
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder="APPLY"
              className="font-mono text-xs max-w-[200px]"
              data-testid={`contact-sync-migration-${key}-apply-confirm-input`}
            />
            <Button
              size="sm"
              onClick={runApply}
              disabled={applying || confirmInput.trim() !== 'APPLY'}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid={`contact-sync-migration-${key}-apply-btn`}
            >
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Play className="h-3.5 w-3.5 mr-1" />}
              Apply migration
            </Button>
          </div>
        )}
        {applyResult && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 space-y-2"
               data-testid={`contact-sync-migration-${key}-apply-result`}>
            <p className="text-sm font-semibold text-emerald-900 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              Applied successfully
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-600">batch_id:</span>
              <code
                className="text-xs bg-white border border-stone-200 rounded px-1.5 py-0.5"
                data-testid={`contact-sync-migration-${key}-batch-id`}
              >
                {applyResult.batch_id}
              </code>
              <Button size="sm" variant="ghost" onClick={copyBatchId} className="h-6 px-2">
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {applyResultKeys.map(([field, label]) => (
                <Stat key={field} label={label} value={applyResult[field]} />
              ))}
            </div>
            <p className="text-[11px] text-emerald-800">
              batch_id copied into the Rollback field below — just click Rollback if you need to undo.
            </p>
          </div>
        )}
      </div>

      {/* Phase 3: Rollback */}
      <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2">
        <div>
          <p className="text-xs font-semibold flex items-center gap-1">
            <Undo2 className="h-3.5 w-3.5 text-amber-700" />
            Step 3 · Rollback
          </p>
          <p className="text-[11px] text-stone-600">
            Undoes an applied batch using the stored backups. Idempotent —
            safe to click twice.
          </p>
        </div>
        <Input
          value={rollbackBatchId}
          onChange={(e) => setRollbackBatchId(e.target.value)}
          placeholder="Paste batch_id here"
          className="font-mono text-xs max-w-md"
          data-testid={`contact-sync-migration-${key}-rollback-batch-input`}
        />
        <Button
          size="sm"
          onClick={runRollback}
          disabled={rollingBack || !rollbackBatchId.trim()}
          variant="outline"
          className="border-amber-500 text-amber-900 hover:bg-amber-100"
          data-testid={`contact-sync-migration-${key}-rollback-btn`}
        >
          {rollingBack ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Undo2 className="h-3.5 w-3.5 mr-1" />}
          Rollback batch
        </Button>
        {rollbackResult && (
          <div className="rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs font-mono"
               data-testid={`contact-sync-migration-${key}-rollback-result`}>
            {JSON.stringify(rollbackResult)}
          </div>
        )}
      </div>
    </div>
  );
}


function Stat({ label, value, highlight, warn }) {
  return (
    <div className={`rounded-md p-2 ${
      warn ? 'bg-amber-50 border border-amber-200'
        : highlight ? 'bg-sky-50 border border-sky-200'
          : 'bg-white border border-stone-200'
    }`}>
      <p className="text-[10px] uppercase tracking-wide text-stone-500">{label}</p>
      <p className={`text-sm font-semibold ${
        warn ? 'text-amber-900' : highlight ? 'text-sky-900' : 'text-stone-900'
      }`}>
        {value ?? 0}
      </p>
    </div>
  );
}
